import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { prefetchRoute } from "@/lib/routePrefetch";

/**
 * Prefetch progressivo guiado pela rolagem da página atual,
 * com curva CONFIGURÁVEL POR ROTA.
 *
 * Cada rota define em qual percentual de rolagem cada destino
 * crítico (/discover, /matches, /chat) deve ser prefetchado.
 * Rotas com forte propensão a um destino disparam o chunk mais
 * cedo; rotas neutras seguem a curva padrão.
 *
 * Ex.: em /matches, o próximo passo provável é /chat — então
 * prefetchamos /chat já em 10% de rolagem.
 *
 * Cada destino baixa no máximo 1x por sessão (prefetchRoute é
 * idempotente). Listener `passive` + rAF throttle.
 */

type Thresholds = Partial<Record<"/discover" | "/matches" | "/chat", number>>;

/** Curva padrão para rotas não mapeadas explicitamente. */
const DEFAULT_THRESHOLDS: Thresholds = {
  "/discover": 0.15,
  "/matches": 0.4,
  "/chat": 0.7,
};

/**
 * Overrides por rota (prefixo). A rota mais específica vence.
 * Valores entre 0 e 1 (proporção de rolagem).
 * Use `null` para desativar prefetch de um destino na rota.
 */
const ROUTE_THRESHOLDS: Array<{ prefix: string; thresholds: Thresholds }> = [
  // Discover já está aberta → priorizar /matches e /chat cedo.
  { prefix: "/discover", thresholds: { "/discover": 0, "/matches": 0.1, "/chat": 0.35 } },

  // Matches → o próximo clique provável é /chat.
  { prefix: "/matches", thresholds: { "/chat": 0.1, "/discover": 0.5 } },

  // Chat → usuário pode voltar para /matches; /discover é menos provável.
  { prefix: "/chat", thresholds: { "/matches": 0.05, "/discover": 0.6 } },

  // Who liked you → conversão típica leva a /chat e /matches.
  { prefix: "/who-liked-you", thresholds: { "/chat": 0.2, "/matches": 0.3, "/discover": 0.55 } },

  // Venues / VenueDetail → exploração; Discover é o próximo passo comum.
  { prefix: "/venue", thresholds: { "/discover": 0.1, "/matches": 0.45, "/chat": 0.7 } },
  { prefix: "/venues", thresholds: { "/discover": 0.1, "/matches": 0.45, "/chat": 0.7 } },

  // Events → mesma lógica de exploração.
  { prefix: "/events", thresholds: { "/discover": 0.15, "/matches": 0.45, "/chat": 0.7 } },

  // Profile / Subscription → usuário está em "gestão"; trio mais tarde.
  { prefix: "/profile", thresholds: { "/discover": 0.25, "/matches": 0.5, "/chat": 0.8 } },
  { prefix: "/my-subscription", thresholds: { "/discover": 0.3, "/matches": 0.55, "/chat": 0.85 } },

  // Landing / Auth → trio antecipado para entrada rápida no app.
  { prefix: "/auth", thresholds: { "/discover": 0.05, "/matches": 0.2, "/chat": 0.45 } },
];

function hasRouteThresholds(pathname: string): boolean {
  const base = pathname.split("?")[0].split("#")[0];
  return ROUTE_THRESHOLDS.some((r) => r.prefix !== "/auth" && (base === r.prefix || base.startsWith(r.prefix + "/") || base.startsWith(r.prefix)));
}

function resolveThresholds(pathname: string): Thresholds {
  const base = pathname.split("?")[0].split("#")[0];
  // Match mais específico (maior prefixo) vence.
  const matched = ROUTE_THRESHOLDS
    .filter((r) => base === r.prefix || base.startsWith(r.prefix + "/") || base.startsWith(r.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return { ...DEFAULT_THRESHOLDS, ...(matched?.thresholds ?? {}) };
}

/** Rotas renderizadas pelo visual novo (ZappyPage). */
export function isZappyPath(pathname: string): boolean {
  const base = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return base === "/" || base === "/admin" || base === "/admin/login";
}

const ScrollProgressivePrefetch = () => {
  const { pathname } = useLocation();
  const tickingRef = useRef(false);
  const firedRef = useRef<Set<string>>(new Set());

  const thresholds = useMemo(() => resolveThresholds(pathname), [pathname]);

  // Resetar marcação ao mudar de rota (cada tela tem sua curva).
  useEffect(() => {
    firedRef.current = new Set();
  }, [pathname]);

  // Telas do visual novo (ZappyPage): a navegação é interna e não usa as
  // páginas antigas de Descobrir/Conexões/Chat; além disso a rolagem acontece
  // dentro de um contêiner, então a página "parece curta" e o prefetch dispararia
  // tudo de uma vez, disputando a rede com a imagem principal da tela.
  // Na entrada (/auth) o destino pós-login também é o visual novo. Fora das
  // rotas mapeadas em ROUTE_THRESHOLDS (ex.: /plans, páginas públicas), a
  // próxima tela não é Descobrir/Conexões/Chat antigos: nada é pré-carregado.
  const isZappyScreen =
    isZappyPath(pathname) || /^\/auth(\/|$)/.test(pathname) || !hasRouteThresholds(pathname);

  useEffect(() => {
    if (typeof window === "undefined" || isZappyScreen) return;

    const evaluate = () => {
      tickingRef.current = false;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      // Página curta sem scroll: dispara tudo imediatamente.
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 1;

      (Object.entries(thresholds) as Array<[string, number | undefined]>).forEach(
        ([route, threshold]) => {
          if (threshold == null) return; // destino desativado nesta rota
          if (firedRef.current.has(route)) return;
          if (ratio >= threshold) {
            firedRef.current.add(route);
            prefetchRoute(route);
          }
        },
      );
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [thresholds, isZappyScreen]);

  return null;
};

export default ScrollProgressivePrefetch;
